/**
 * Remembers the user's response to the "Create a passkey?" prompt (SEE-213).
 * Passkeys are opt-in and never enforced: "Not now" always continues to the
 * dashboard and only skips the prompt for the current login session — the
 * prompt may show again on a later login. Only the explicit "Don't ask again"
 * choice (or having a passkey) stops it for good (SEE-1146; the original
 * three-decline cap on "Not now" is gone).
 *
 * Stored per Cognito sub in localStorage. NOTE: this is per-device; a follow-up
 * can promote it to a server-side user preference so the created/dismissed
 * flags follow the user across devices (updateMyPreferences already exists).
 */

interface PasskeyPromptState {
  created: boolean;
  dismissed: boolean;
}

const keyFor = (sub: string) => `passkeyPrompt:${sub}`;

function read(sub: string): PasskeyPromptState {
  try {
    const raw = localStorage.getItem(keyFor(sub));
    if (!raw) return { created: false, dismissed: false };
    const parsed = JSON.parse(raw) as Partial<PasskeyPromptState> & { optOuts?: number };
    return {
      created: Boolean(parsed.created),
      // Legacy SEE-213 shape stored the explicit "Don't ask again" by
      // fast-forwarding an optOuts counter to its cap of 3; keep those users
      // dismissed rather than resurface a prompt they explicitly ended.
      dismissed: Boolean(parsed.dismissed) || (Number(parsed.optOuts) || 0) >= 3,
    };
  } catch {
    return { created: false, dismissed: false };
  }
}

function write(sub: string, state: PasskeyPromptState): void {
  try {
    localStorage.setItem(keyFor(sub), JSON.stringify(state));
  } catch {
    /* storage unavailable — prompt simply won't be remembered this session */
  }
}

/** Whether the post-login prompt should be shown (no passkey yet, not dismissed). */
export function shouldShowPasskeyPrompt(sub: string): boolean {
  const state = read(sub);
  return !state.created && !state.dismissed;
}

export function recordPasskeyCreated(sub: string): void {
  write(sub, { ...read(sub), created: true });
}

/**
 * SEE-226: forget that this user has a passkey, so the post-login prompt offers
 * to create one again. Called when a removal leaves them with none — otherwise
 * the `created` flag written at registration would suppress the prompt forever
 * on this device while the server says they have zero passkeys.
 *
 * Deliberately leaves `dismissed` alone: an explicit "Don't ask again" is the
 * user's standing choice and removing a passkey is not a request to undo it.
 */
export function clearPasskeyCreated(sub: string): void {
  write(sub, { ...read(sub), created: false });
}

/**
 * SEE-226: clear BOTH suppressors, for the deliberate act of removing your last
 * passkey. `created` is now false in fact, and a "Don't ask again" from earlier
 * has nothing left to protect — the user has no passkey, so the offer is
 * relevant again and they can dismiss it a second time if they still mean it.
 * Load-time healing deliberately does NOT do this: only an explicit removal on
 * this device counts as reopening the question.
 */
export function clearPasskeyPromptSuppression(sub: string): void {
  write(sub, { created: false, dismissed: false });
}

/**
 * Permanently stop prompting this user on this device (the explicit "Don't ask
 * again" choice) — without marking a passkey as created (they still have none).
 */
export function stopAskingPasskey(sub: string): void {
  write(sub, { ...read(sub), dismissed: true });
}
